import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/chronovela.css';
import { resolveAppComponent } from './appEntry';
import { FirebaseProvider } from '../components/FirebaseProvider';
import { LandingPage } from '../components/LandingPage';
import { TermsPage, PrivacyPage, RefundPolicyPage, AiDisclaimerPage } from '../components/LegalPages';
import { HelpPage } from '../components/HelpPage';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { isAppAccessBlocked } from './utils/launchGate';
import { withPublicSiteLayout } from '../components/PublicSiteLayout';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Could not find root element to mount to');
}

const root = ReactDOM.createRoot(rootElement);

const path = window.location.pathname;

function keepOnLanding(): void {
  // Soft client-side redirect: rewrite the URL to the homepage so the public
  // never lands on a blocked route. (True 302s for known app/checkout routes
  // are also configured in firebase.json.)
  if (typeof window !== 'undefined' && window.history?.replaceState) {
    window.history.replaceState(null, '', '/');
  }
}

// Routes that always stay public: the landing page, help, and legal pages.
const PUBLIC_CONTENT_ROUTES: Record<string, React.ComponentType> = {
  '/': withPublicSiteLayout(LandingPage),
  '/help': withPublicSiteLayout(HelpPage),
  '/terms': withPublicSiteLayout(TermsPage),
  '/privacy': withPublicSiteLayout(PrivacyPage),
  '/refund-policy': withPublicSiteLayout(RefundPolicyPage),
  '/ai-disclaimer': withPublicSiteLayout(AiDisclaimerPage),
};

let ComponentToRender: React.ComponentType = PUBLIC_CONTENT_ROUTES['/']!;

if (path === '/app') {
  if (isAppAccessBlocked()) {
    // Pre-launch: keep the public on the landing page instead of the app.
    keepOnLanding();
    ComponentToRender = PUBLIC_CONTENT_ROUTES['/']!;
  } else {
    const AppComponent = resolveAppComponent();
    ComponentToRender = () => (
      <ErrorBoundary>
        <FirebaseProvider>
          <AppComponent />
        </FirebaseProvider>
      </ErrorBoundary>
    );
  }
} else if (PUBLIC_CONTENT_ROUTES[path]) {
  ComponentToRender = PUBLIC_CONTENT_ROUTES[path];
} else if (isAppAccessBlocked()) {
  // Any other route (dashboard, onboarding, workspace, checkout, etc.) is an
  // in-app destination. While locked down, send the visitor to the homepage.
  keepOnLanding();
  ComponentToRender = PUBLIC_CONTENT_ROUTES['/']!;
}

root.render(
  <React.StrictMode>
    <ComponentToRender />
  </React.StrictMode>,
);
